import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";

// Threshold: when active boxes exceed this, skip-frame updates kick in
const SKIP_FRAME_THRESHOLD = 5;

/**
 * WinBoxController - Manages per-cell win box spine animations
 *
 * Architecture: Pre-warmed spine pool with manual ticker updates
 * - One spine instance per cell (REEL_COUNT × SYMBOLS_VISIBLE = 20 cells)
 * - Each spine plays the "win" animation from win_box.json
 * - autoUpdate disabled on all spines — manual update via shared ticker
 * - When 6+ boxes active: updates every 2nd frame to halve bone processing cost
 * - Active-only tracking: show/hide/update only touches relevant spines
 */
class WinBoxController {
  constructor(container, symbolWidth, symbolHeight, reelSpacing) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    // One spine instance per cell, keyed by "reelIndex_symbolIndex"
    this.winBoxSpines = {};
    this.spineContainer = new PIXI.Container();
    this.initialized = false;

    // Track currently active cell keys (avoids iterating all spines)
    this.activeCellKeys = [];

    // Skip-frame update state
    this._frameCounter = 0;
    this._tickerBound = this._onTick.bind(this);
    this._tickerActive = false;

    // Internal caches
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    // Add spine container to main container
    this.container.addChild(this.spineContainer);

    // Initialize all spines
    this.initializeSpines();
  }

  /**
   * Initialize spine instances — one per grid cell, all pre-warmed
   */
  initializeSpines() {
    if (!this.symbolWidth || !this.symbolHeight) {
      return;
    }

    // Create a probe spine to verify the asset is available
    const probeSpine = this.createWinBoxSpine();
    if (!probeSpine || !probeSpine.skeleton || !probeSpine.state) {
      return;
    }

    // Use probe as first cell's instance
    this.warmAndStoreSpine(probeSpine, 0, 0);

    // Create remaining spines for all other cells
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        // Skip the first cell (already used probe spine)
        if (reelIndex === 0 && symbolIndex === 0) continue;

        const spine = this.createWinBoxSpine();
        if (!spine || !spine.state) continue;

        this.warmAndStoreSpine(spine, reelIndex, symbolIndex);
      }
    }

    // Start with container hidden (GPU still processes at alpha 0.001)
    this.spineContainer.alpha = 0.001;

    // Schedule GPU warmup cleanup — wait 3 frames for renderer to compile
    // shaders and upload vertex/texture data, then pause all spines cleanly
    this.scheduleWarmupCleanup();

    this.initialized = true;
  }

  /**
   * Wait 3 GPU frames after warmup to ensure PixiJS renderer has drawn
   * all spine meshes and compiled shaders/uploaded buffers. Then pause cleanly.
   * (Same pattern as OverlayReelController.scheduleWarmupCleanup)
   */
  scheduleWarmupCleanup() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const cellKeys = Object.keys(this.winBoxSpines);
          for (let i = 0; i < cellKeys.length; i++) {
            const spine = this.winBoxSpines[cellKeys[i]];
            if (spine?.state) {
              spine.state.clearTracks();
              spine.state.timeScale = 0;
              spine.autoUpdate = false;
              spine.alpha = 0.001;
            }
          }
          this.spineContainer.alpha = 0.001;
        });
      });
    });
  }

  /**
   * Warm up a spine instance and store it in the pool
   * @param {Spine} spine - The spine instance to warm
   * @param {number} reelIndex - Reel index (0-4)
   * @param {number} symbolIndex - Symbol row index (0-3)
   */
  warmAndStoreSpine(spine, reelIndex, symbolIndex) {
    // autoUpdate OFF — we control updates manually via ticker
    spine.autoUpdate = false;
    this.spineContainer.addChild(spine);

    // GPU invisible but still in render pipeline
    spine.alpha = 0.001;

    // Position at the cell coordinates
    this.positionSpine(spine, reelIndex, symbolIndex);

    // Scale to fit symbol cell
    this.scaleSpine(spine);

    // Set win animation → triggers shader compilation and mesh creation
    spine.state.setAnimation(0, "win", true);
    spine.state.update(0.016);

    // Populate bone/slot hierarchy for GPU
    try {
      spine.skeleton.updateWorldTransform();
    } catch (e) {
      // Physics not initialized on some spines — safe to continue
    }

    // Don't pause yet — leave spine rendering at alpha 0.001 so GPU
    // compiles shaders during the 3-frame warmup window.
    // scheduleWarmupCleanup() will clear tracks and pause after 3 frames.

    const cellKey = `${reelIndex}_${symbolIndex}`;
    this.winBoxSpines[cellKey] = spine;
  }

  /**
   * Position a spine at a specific cell
   */
  positionSpine(spine, reelIndex, symbolIndex) {
    // Center of reel: reelIndex offset from center, using reelSpacing
    const x = this.reelSpacing * (reelIndex - Math.floor(Settings.REEL_COUNT / 2));
    // Y position: row offset from top of reel set plus half symbol for centering
    const y =
      this.symbolHeight * symbolIndex -
      (this.symbolHeight * (Settings.SYMBOLS_VISIBLE - 1)) / 2;

    spine.position.set(x, y);
  }

  /**
   * Scale spine to fill symbol cell dimensions.
   * Uses the same approach as OverlayReelController.calculateSpineScale:
   * scale = containerHeight / Settings.SYMBOL_HEIGHT (native design size)
   */
  scaleSpine(spine) {
    const heightScale = this.symbolHeight / Settings.SYMBOL_HEIGHT;
    spine.scale.set(heightScale);
  }

  /**
   * Create a single win box spine instance
   */
  createWinBoxSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_box-skeleton";
      const atlasAlias = "win_box-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!spine || !spine.skeleton || !spine.state) {
        return null;
      }

      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Shared ticker callback — updates only active spines manually
   * When 1-5 boxes: updates every frame (smooth)
   * When 6+ boxes: updates every 2nd frame (halves bone cost)
   */
  _onTick() {
    const activeCount = this.activeCellKeys.length;
    if (activeCount === 0) return;

    this._frameCounter++;

    // Skip every other frame when many boxes are active
    if (activeCount > SKIP_FRAME_THRESHOLD && this._frameCounter % 2 !== 0) {
      return;
    }

    // Compute delta time (in seconds)
    const dt = PIXI.Ticker.shared.deltaMS / 1000;
    // When skipping frames, compensate so animation speed stays correct
    const compensatedDt = activeCount > SKIP_FRAME_THRESHOLD ? dt * 2 : dt;

    for (let i = 0; i < activeCount; i++) {
      const cellKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (spine && spine.state && spine.state.timeScale > 0) {
        spine.update(compensatedDt);
      }
    }
  }

  /** Start the manual update ticker */
  _startTicker() {
    if (!this._tickerActive) {
      this._frameCounter = 0;
      PIXI.Ticker.shared.add(this._tickerBound);
      this._tickerActive = true;
    }
  }

  /** Stop the manual update ticker */
  _stopTicker() {
    if (this._tickerActive) {
      PIXI.Ticker.shared.remove(this._tickerBound);
      this._tickerActive = false;
      this._frameCounter = 0;
    }
  }

  /**
   * Show win boxes at specified positions
   * @param {Array<[number, number]>} positions - Array of [reelIndex, symbolIndex] pairs
   */
  showWinBoxes(positions) {
    if (!this.initialized) return;
    if (!Array.isArray(positions) || positions.length === 0) return;

    // Hide container during setup
    this.spineContainer.alpha = 0.001;

    // Stop ticker while setting up
    this._stopTicker();

    // Pause previously active spines
    for (let i = 0; i < this.activeCellKeys.length; i++) {
      const prevKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[prevKey];
      if (spine && spine.state) {
        spine.alpha = 0.001;
        spine.state.timeScale = 0;
      }
    }

    // Activate the requested cell spines
    const validKeys = [];
    for (let i = 0; i < positions.length; i++) {
      const [reelIndex, symbolIndex] = positions[i];
      const cellKey = `${reelIndex}_${symbolIndex}`;
      const spine = this.winBoxSpines[cellKey];
      if (!spine || !spine.state) continue;

      // Reset animation to frame 0 for perfect synchronization
      spine.state.clearTracks();
      spine.state.setAnimation(0, "win", true);
      spine.state.update(0);

      // CRITICAL: Apply animation to skeleton BEFORE making visible
      // Without this, the `scale` bone stays at setup-pose (1.0) for 1 frame
      // instead of the animation value (0.5), causing a 2× size flash
      spine.state.apply(spine.skeleton);
      try {
        spine.skeleton.updateWorldTransform();
      } catch (e) {
        // Physics constraint not initialized — safe to ignore
      }

      spine.alpha = 1;
      spine.state.timeScale = 1;
      validKeys.push(cellKey);
    }

    this.activeCellKeys = validKeys;

    // Start manual ticker for active spine updates
    this._startTicker();

    // Show container — all requested boxes appear at once
    this.spineContainer.alpha = 1;
  }

  /**
   * Show win boxes for multiple lines simultaneously (for "show all wins" phase)
   * @param {Array<Array<[number, number]>>} allLinePositions - Array of position arrays per line
   */
  showAllWinBoxes(allLinePositions) {
    if (!this.initialized) return;
    if (!Array.isArray(allLinePositions) || allLinePositions.length === 0)
      return;

    // Deduplicate positions across all lines
    const positionSet = new Set();
    const uniquePositions = [];

    for (let i = 0; i < allLinePositions.length; i++) {
      const linePositions = allLinePositions[i];
      if (!Array.isArray(linePositions)) continue;

      for (let j = 0; j < linePositions.length; j++) {
        const [reelIndex, symbolIndex] = linePositions[j];
        const key = `${reelIndex}_${symbolIndex}`;
        if (!positionSet.has(key)) {
          positionSet.add(key);
          uniquePositions.push([reelIndex, symbolIndex]);
        }
      }
    }

    this.showWinBoxes(uniquePositions);
  }

  /**
   * Hide all active win boxes
   */
  hideWinBoxes() {
    // Stop ticker
    this._stopTicker();

    // Hide container
    this.spineContainer.alpha = 0.001;

    // Pause and reset only the active spines
    for (let i = 0; i < this.activeCellKeys.length; i++) {
      const cellKey = this.activeCellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (spine && spine.state) {
        spine.state.timeScale = 0;
        spine.state.clearTracks();
        if (spine.skeleton?.setToSetupPose) {
          spine.skeleton.setToSetupPose();
        }
        spine.alpha = 0.001;
      }
    }

    this.activeCellKeys = [];
  }

  /**
   * Update spine dimensions and rescale for resize events
   */
  updateDimensionsAndRescale(symbolWidth, symbolHeight, reelSpacing) {
    if (
      this.symbolWidth === symbolWidth &&
      this.symbolHeight === symbolHeight &&
      this.reelSpacing === reelSpacing
    ) {
      return;
    }

    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelSpacing = reelSpacing;

    if (!this.initialized) return;

    // Reposition and rescale all spines
    const cellKeys = Object.keys(this.winBoxSpines);
    for (let i = 0; i < cellKeys.length; i++) {
      const cellKey = cellKeys[i];
      const spine = this.winBoxSpines[cellKey];
      if (!spine) continue;

      const [reelIndex, symbolIndex] = cellKey.split("_").map(Number);
      this.positionSpine(spine, reelIndex, symbolIndex);
      this.scaleSpine(spine);
    }
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.initialized && Object.keys(this.winBoxSpines).length > 0;
  }

  /**
   * Clean up all spine instances and resources
   */
  cleanup() {
    this._stopTicker();

    const cellKeys = Object.keys(this.winBoxSpines);
    for (let i = 0; i < cellKeys.length; i++) {
      const spine = this.winBoxSpines[cellKeys[i]];
      if (spine) {
        if (spine.state) {
          spine.state.clearTracks();
          spine.state.clearListeners();
        }
        if (spine.parent) {
          spine.parent.removeChild(spine);
        }
        spine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }
    this.winBoxSpines = {};

    if (this.spineContainer) {
      if (this.spineContainer.parent) {
        this.spineContainer.parent.removeChild(this.spineContainer);
      }
      this.spineContainer.destroy({ children: true });
      this.spineContainer = null;
    }

    this.activeCellKeys = [];
    this.initialized = false;
  }
}

export default WinBoxController;
